import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import { useSeo } from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { getHotVideos, searchVideos } from "@/utils/video";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [input, setInput] = useState(q);
  const [tag, setTag] = useState("全部");

  useEffect(() => {
    setInput(q);
    setTag("全部");
  }, [q]);

  useSeo({
    title: q ? `搜索「${q}」 - 糖心Vlog` : "搜索视频 - 糖心Vlog",
    description: q
      ? `糖心Vlog 中与「${q}」相关的视频搜索结果，帮你快速找到感兴趣的日常、旅行与见解内容。`
      : "在糖心Vlog 搜索你感兴趣的视频内容。",
    keywords: `${q},糖心Vlog搜索,视频搜索,生活视频`,
    path: "/search",
  });

  const matched = useMemo(() => (input.trim() ? searchVideos(input) : []), [input]);

  const tags = useMemo(
    () => ["全部", ...Array.from(new Set(matched.flatMap((v) => v.tags)))],
    [matched],
  );

  const results = useMemo(
    () => (tag === "全部" ? matched : matched.filter((v) => v.tags.includes(tag))),
    [matched, tag],
  );

  const hot = getHotVideos(3);

  const updateQuery = (value: string) => {
    setInput(value);
    if (value.trim()) {
      setSearchParams({ q: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="font-medium text-foreground-900">搜索结果</span>
      </nav>

      <h1 className="mt-6 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
        {q ? `搜索「${q}」` : "搜索视频"}
      </h1>

      {/* search box */}
      <div className="mt-5 flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-2 pl-4 focus-within:border-primary-300">
        <i className="ri-search-line text-lg text-foreground-400" />
        <input
          value={input}
          onChange={(e) => updateQuery(e.target.value)}
          placeholder="输入关键词，如 旅行、独居、海岛…"
          className="w-full bg-transparent py-2 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 md:text-base"
        />
        {input && (
          <button
            type="button"
            onClick={() => updateQuery("")}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
            aria-label="清空"
          >
            <i className="ri-close-line text-lg" />
          </button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          {q ? (
            <>
              共找到 <span className="font-semibold text-foreground-900">{results.length}</span> 个结果
            </>
          ) : (
            "输入关键词开始搜索，或从下方热门分类快速进入"
          )}
        </p>
      </div>

      {/* tag filter */}
      {matched.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground-700">按标签筛选：</span>
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTag(t)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                tag === t
                  ? "bg-secondary-500 font-medium text-background-50"
                  : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {/* results */}
      {results.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((v, i) => (
            <VideoCard key={v.id} video={v} index={i} />
          ))}
        </div>
      ) : (
        <div className="mt-8">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <i className="ri-search-eye-line text-5xl text-foreground-400" />
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950">
              {q ? `未找到与「${q}」相关的视频` : "还没有开始搜索"}
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              {q ? "换个关键词试试，或看看下面的热门推荐。" : "试试搜索「旅行」「独居」「治愈」等关键词。"}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {input && (
                <button
                  type="button"
                  onClick={() => updateQuery("")}
                  className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  清空搜索项
                </button>
              )}
              <Link
                to="/"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300"
              >
                返回首页
              </Link>
            </div>
          </div>

          {/* recommendations */}
          <div className="mt-10">
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">推荐分类</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                    <i className={c.icon} />
                  </span>
                  <span className="text-sm font-medium text-foreground-950">{c.name}</span>
                  <i className="ri-arrow-right-line ml-auto text-foreground-400" />
                </Link>
              ))}
            </div>

            <h2 className="mb-4 mt-10 font-heading text-lg font-bold text-foreground-950">
              热门视频
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {hot.map((v, i) => (
                <VideoCard key={v.id} video={v} index={i} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}